export type FramingBox = {
  xMin: number;
  yMin: number;
  xMax: number;
  yMax: number;
};

export type FramingTransform = {
  kind?: string | null;
  crop?: FramingBox | null;
  contentBox?: FramingBox | null;
  orientation?: string | null;
  scaleX?: number | null;
  scaleY?: number | null;
  offsetX?: number | null;
  offsetY?: number | null;
  raw: Record<string, unknown>;
};

export type FramingCoordinateSpace = {
  id: string;
  stage: string;
  assetKey?: string | null;
  assetHash?: string | null;
  width?: number | null;
  height?: number | null;
  mimeType?: string | null;
  aspectRatio?: string | number | null;
  parentSpaceId?: string | null;
  transformFromParent?: FramingTransform | null;
  assetHashKind?: string | null;
  retentionState?: "retained" | "ephemeral" | "external" | "unknown" | null;
  viewable?: boolean | null;
  evidenceRole?: "actual_applied" | "proposed_enforce" | "shared_source" | null;
  timelineStartSeconds?: number | null;
  timelineEndSeconds?: number | null;
};

export type FramingObservation = {
  subjectId?: string | null;
  label?: string | null;
  kind?: string | null;
  priority?: string | null;
  bbox: FramingBox;
  frameIndex?: number | null;
  timestampSeconds?: number | null;
  confidence?: number | null;
  detector?: string | null;
  identityScore?: number | null;
  coordinateSpaceId: string;
  associationMethod?: string | null;
  associationReasonCode?: string | null;
  intentSource?: string | null;
  declarationReason?: string | null;
  identityStatus?: string | null;
};

export type FramingIntentSubject = {
  subjectId?: string | null;
  label?: string | null;
  kind?: string | null;
  priority?: string | null;
  identityRequired?: boolean | null;
  intentSource?: string | null;
  declarationReason?: string | null;
};

export type FramingIntentSummary = {
  mode?: string | null;
  source?: string | null;
  shotSize?: string | null;
  trackingAllowed?: boolean | null;
  cropAllowed?: boolean | null;
  fallbackPolicy: string[];
  confidence?: number | null;
  subjects: FramingIntentSubject[];
};

export type FramingSceneKind = "i2v" | "still" | "video" | "unknown";

export type FramingEvidenceMode = "actual" | "proposed" | "unknown";

export type ProviderInputReceipt = {
  schemaVersion?: string | null;
  strategy?: string | null;
  sourceCoordinateSpace?: string | null;
  resizedCoordinateSpace?: string | null;
  targetCoordinateSpace?: string | null;
  cropWindowPixelsSpace?: string | null;
  cropWindowNormalizedSpace?: string | null;
  sourceWidth?: number | null;
  sourceHeight?: number | null;
  resizedWidth?: number | null;
  resizedHeight?: number | null;
  targetWidth?: number | null;
  targetHeight?: number | null;
  outputAspectRatio?: string | null;
  requestedAspectRatio?: string | null;
  cropWindow?: FramingBox | null;
  cropWindowPixels: number[];
  sourceSha256?: string | null;
  providerInputSha256?: string | null;
  framingTraceId?: string | null;
};

export type FramingCropPathPoint = {
  timeSeconds: number;
  frameIndex?: number | null;
  window: FramingBox;
};

export type FramingEffectViewport = {
  effectType?: string | null;
  coordinateSpaceId?: string | null;
  cropWindow?: FramingBox | null;
  cropPath: FramingCropPathPoint[];
  visibleContentBox?: FramingBox | null;
  zoomFactor?: number | null;
  anchor?: readonly [number, number] | null;
};

export type FramingSafeZone = {
  kind: "provider_input" | "provider_output" | "delivery" | "unknown";
  coordinateSpaceId?: string | null;
  box: FramingBox;
};

export type FramingDecision = {
  strategy?: string | null;
  coordinateSpaceId?: string | null;
  cropWindow?: FramingBox | null;
  cropPath: FramingCropPathPoint[];
  deliverySafeZone?: FramingBox | null;
  safeZones: FramingSafeZone[];
  protectedRegion?: FramingBox | null;
  effectViewport?: FramingEffectViewport | null;
  visibleContentBox?: FramingBox | null;
  fps?: number | null;
  protectedSubjectIds: string[];
  sacrificedPreferredSubjectIds: string[];
  fallbackReason?: string | null;
};

export type AppliedFramingReceipt = {
  schemaVersion?: string | null;
  receiptId?: string | null;
  sceneId?: string | null;
  planId?: string | null;
  planDigest?: string | null;
  planRevision?: number | null;
  attemptId?: string | null;
  taskId?: string | null;
  renderer?: string | null;
  workerId?: string | null;
  sourceCoordinateSpaceId?: string | null;
  sourceAssetHash?: string | null;
  sourceWidth?: number | null;
  sourceHeight?: number | null;
  deliveryWidth?: number | null;
  deliveryHeight?: number | null;
  strategy?: string | null;
  cropWindow?: FramingBox | null;
  cropPath: FramingCropPathPoint[];
  effectViewport?: FramingEffectViewport | null;
  visibleContentBox?: FramingBox | null;
  fps?: number | null;
  durationSeconds?: number | null;
  appliedAt?: string | null;
  outputAssetHash?: string | null;
  status?: string | null;
  issueCodes: string[];
};

export type FramingQcRecord = {
  status?: string | null;
  issueCodes: string[];
  semanticVerdict?: string | null;
  deliveredSubjectEvidence: FramingDeliveredSubjectEvidence[];
  checkedCoordinateSpaceId?: string | null;
  sampleCount?: number | null;
  repairAttempt?: number | null;
  occurredAt?: string | null;
  metrics?: Record<string, number | string | boolean | null> | null;
};

export type FramingDeliveredSubjectEvidence = {
  subjectId?: string | null;
  kind?: string | null;
  sampleIndex?: number | null;
  sceneTimestampSeconds?: number | null;
  deliveryTimestampSeconds?: number | null;
  bbox: FramingBox;
  confidence?: number | null;
  matchStatus?: string | null;
  detector?: string | null;
  model?: string | null;
  modelVersion?: string | null;
  coordinateSpaceId?: string | null;
};

export type FramingVerification = {
  status: "verified" | "mismatch" | "unknown" | "proposed";
  issues: string[];
};

export type FramingArtifact = {
  id: string;
  kind?: string | null;
  stage?: string | null;
  bucket?: string | null;
  key?: string | null;
  contentType?: string | null;
  sha256?: string | null;
  planDigest?: string | null;
  attemptId?: string | null;
  taskId?: string | null;
  coordinateSpaceId?: string | null;
  url?: string | null;
  expiresIn?: number | null;
  /** Server-authoritative absolute signing metadata (CROP-WB-002). */
  signedAt?: string | null;
  urlExpiresAt?: string | null;
  retentionClass?: string | null;
  expiresAt?: string | null;
  evidenceRole?: "actual_applied" | "proposed_enforce" | "shared_source" | null;
};

/** Allowlisted evidence availability (CROP-WB-002). Unknown codes map to
 * "unknown" and the raw safe code stays available for Engineering details. */
export type FramingAvailability =
  | "available"
  | "not_applicable"
  | "not_recorded"
  | "ephemeral"
  | "external"
  | "expired"
  | "deleted"
  | "missing"
  | "hash_mismatch"
  | "correlation_mismatch"
  | "dimension_mismatch"
  | "signing_failed"
  | "decode_unsupported"
  | "network_error"
  | "projection_only"
  | "legacy_mutability_unknown"
  | "unknown";

export const FRAMING_AVAILABILITY_VALUES: readonly FramingAvailability[] = [
  "available",
  "not_applicable",
  "not_recorded",
  "ephemeral",
  "external",
  "expired",
  "deleted",
  "missing",
  "hash_mismatch",
  "correlation_mismatch",
  "dimension_mismatch",
  "signing_failed",
  "decode_unsupported",
  "network_error",
  "projection_only",
  "legacy_mutability_unknown",
  "unknown",
];

export type FramingAttemptStatus =
  | "accepted"
  | "rejected"
  | "failed"
  | "repaired"
  | "fallback"
  | "unknown";

export type FramingAttemptSummary = {
  attemptId: string;
  ordinal: number | null;
  status: FramingAttemptStatus;
  startedAt: string | null;
  completedAt: string | null;
  planDigest: string | null;
  receiptDigest: string | null;
  reportDigest: string | null;
  evidenceAvailability: FramingAvailability;
  rawAvailability: string | null;
  isDefault: boolean;
};

export type FramingCapabilities = {
  exactSource: boolean;
  workingSource: boolean;
  providerInput: boolean;
  generatedRoot: boolean;
  appliedReceipt: boolean;
  deliveryPixels: boolean;
  subjectVerdicts: boolean;
  attemptHistory: boolean;
  preciseVideoTiming: boolean;
  contactSheetFallback: boolean;
};

export type FramingStageAvailabilityKey =
  | "source_native"
  | "source_working"
  | "provider_input"
  | "provider_output"
  | "post_effect"
  | "encoded_delivery";

export function parseFramingAvailability(value: unknown): FramingAvailability {
  const text = typeof value === "string" ? value.trim() : "";
  return (FRAMING_AVAILABILITY_VALUES as readonly string[]).includes(text)
    ? (text as FramingAvailability)
    : "unknown";
}

export type FramingHistoryEvent = {
  id: string;
  eventType: string;
  status?: string | null;
  strategy?: string | null;
  planId?: string | null;
  planRevision?: number | null;
  planDigest?: string | null;
  attemptId?: string | null;
  taskId?: string | null;
  receiptId?: string | null;
  occurredAt?: string | null;
};

export type FramingSceneInspector = {
  schemaVersion: string;
  sessionId: string;
  sceneId: string;
  sceneType?: string | null;
  sourceSceneType?: string | null;
  finalSceneType?: string | null;
  sceneKind: FramingSceneKind;
  framingMode?: string | null;
  evidenceMode: FramingEvidenceMode;
  providerTaskExpected: boolean | null;
  providerReceiptExpected: boolean | null;
  attemptId?: string | null;
  current: FramingHistoryEvent & {
    checkedCoordinateSpaceId?: string | null;
    configReleaseId?: string | null;
    configDigest?: string | null;
    configSchemaVersion?: string | null;
  };
  intent: FramingIntentSummary | null;
  providerInputReceipt: ProviderInputReceipt | null;
  provenance: Record<string, unknown>;
  coordinateSpaces: FramingCoordinateSpace[];
  observations: FramingObservation[];
  decision: FramingDecision | null;
  appliedReceipt: AppliedFramingReceipt | null;
  qcHistory: FramingQcRecord[];
  history: FramingHistoryEvent[];
  artifacts: FramingArtifact[];
  source: Record<string, unknown> | null;
  report: Record<string, unknown> | null;
  /** Additive CROP-WB-002 contract; absent on older API responses. */
  attempts: FramingAttemptSummary[];
  attemptsTotal: number | null;
  attemptsTruncated: boolean;
  attemptAvailability: FramingAvailability | null;
  capabilities: FramingCapabilities | null;
  stageAvailability: Partial<
    Record<FramingStageAvailabilityKey, FramingAvailability>
  >;
  historyReturned: number | null;
  historyCap: number | null;
  historyTruncated: boolean;
};

export type FramingStageKey =
  | "raw"
  | "working"
  | "provider"
  | "generated"
  | "pre_effect"
  | "encoded";

export type FramingStageView = {
  key: FramingStageKey;
  label: string;
  description: string;
  coordinateSpace: FramingCoordinateSpace | null;
  artifact: FramingArtifact | null;
};

const STAGES: ReadonlyArray<{
  key: FramingStageKey;
  label: string;
  description: string;
  aliases: readonly string[];
}> = [
  {
    key: "raw",
    label: "Received / raw",
    description: "Exact received pixels before non-destructive normalization.",
    aliases: ["input_raw", "received", "raw", "source_raw"],
  },
  {
    key: "working",
    label: "Working",
    description: "EXIF-upright, content-addressed working source.",
    aliases: ["input_working", "working", "working_source"],
  },
  {
    key: "provider",
    label: "Provider input",
    description: "Exact pixels and aspect handed to the I2V provider.",
    aliases: ["provider_input", "provider", "i2v_input"],
  },
  {
    key: "generated",
    label: "Generated clip",
    description: "Provider output, treated as an independent coordinate root.",
    aliases: ["generated_clip", "generated", "provider_output"],
  },
  {
    key: "pre_effect",
    label: "Pre-effect delivery",
    description: "Delivery canvas before zoom, slide, or postcard effects.",
    aliases: [
      "pre_effect_delivery",
      "delivery_canvas",
      "delivery_pre_effect",
      "parent_prepared_delivery",
      "post_crop",
    ],
  },
  {
    key: "encoded",
    label: "Encoded delivery",
    description: "Worker-produced pixels sampled from the encoded scene.",
    aliases: [
      "encoded_delivery",
      "final_delivery",
      "rendered_delivery",
      "output",
    ],
  },
] as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function record(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}

function stringValue(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function scalarStringValue(value: unknown): string | null {
  return (
    stringValue(value) ??
    (typeof value === "number" && Number.isFinite(value) ? String(value) : null)
  );
}

function numberValue(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function booleanValue(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function stringList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map(stringValue).filter((item): item is string => item != null)
    : [];
}

function numberList(value: unknown): number[] {
  return Array.isArray(value)
    ? value.map(numberValue).filter((item): item is number => item != null)
    : [];
}

export function parseFramingBox(value: unknown): FramingBox | null {
  if (Array.isArray(value) && value.length >= 4) {
    const [xMin, yMin, xMax, yMax] = value.map(numberValue);
    return xMin != null && yMin != null && xMax != null && yMax != null
      ? { xMin, yMin, xMax, yMax }
      : null;
  }
  const source = record(value);
  const xMin = numberValue(
    source.x_min ?? source.xmin ?? source.x1 ?? source.left ?? source.x,
  );
  const yMin = numberValue(
    source.y_min ?? source.ymin ?? source.y1 ?? source.top ?? source.y,
  );
  const xMax = numberValue(
    source.x_max ?? source.xmax ?? source.x2 ?? source.right,
  );
  const yMax = numberValue(
    source.y_max ?? source.ymax ?? source.y2 ?? source.bottom,
  );
  const width = numberValue(source.width ?? source.w);
  const height = numberValue(source.height ?? source.h);
  if (xMin == null || yMin == null) return null;
  const resolvedXMax = xMax ?? (width == null ? null : xMin + width);
  const resolvedYMax = yMax ?? (height == null ? null : yMin + height);
  return resolvedXMax != null && resolvedYMax != null
    ? { xMin, yMin, xMax: resolvedXMax, yMax: resolvedYMax }
    : null;
}

function parseCropPath(value: unknown, fps = 30): FramingCropPathPoint[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item, index): FramingCropPathPoint | null => {
      if (Array.isArray(item) && item.length >= 5) {
        const timeSeconds = numberValue(item[0]);
        const window = parseFramingBox(item.slice(1, 5));
        return timeSeconds != null && window ? { timeSeconds, window } : null;
      }
      const point = record(item);
      const window = parseFramingBox(
        point.window ?? point.crop_window ?? point.bbox ?? point,
      );
      if (!window) return null;
      return {
        timeSeconds:
          numberValue(
            point.time_seconds ??
              point.timestamp_seconds ??
              point.t ??
              point.time,
          ) ??
          (numberValue(point.frame ?? point.frame_index) ?? index) /
            Math.max(1, fps),
        frameIndex: numberValue(point.frame ?? point.frame_index),
        window,
      };
    })
    .filter((item): item is FramingCropPathPoint => item != null)
    .sort((left, right) => left.timeSeconds - right.timeSeconds);
}

function parseTransform(value: unknown): FramingTransform | null {
  if (!isRecord(value)) return null;
  return {
    kind: stringValue(value.kind ?? value.type),
    crop: parseFramingBox(value.crop ?? value.crop_window ?? value.crop_box),
    contentBox: parseFramingBox(value.content_box ?? value.visible_content_box),
    orientation: stringValue(value.orientation ?? value.exif_orientation),
    scaleX: numberValue(value.scale_x),
    scaleY: numberValue(value.scale_y),
    offsetX: numberValue(value.offset_x),
    offsetY: numberValue(value.offset_y),
    raw: value,
  };
}

function parseEffectViewport(
  value: unknown,
  fallbackFps = 30,
): FramingEffectViewport | null {
  if (!isRecord(value)) return null;
  const legacyBox = parseFramingBox(value);
  const fps = numberValue(value.fps) ?? fallbackFps;
  const anchor = Array.isArray(value.anchor)
    ? value.anchor.map(numberValue).slice(0, 2)
    : [];
  return {
    effectType: stringValue(value.effect_type ?? value.type),
    coordinateSpaceId: stringValue(value.coordinate_space_id ?? value.space_id),
    cropWindow: parseFramingBox(value.crop_window ?? value.window) ?? legacyBox,
    cropPath: parseCropPath(value.crop_path, fps),
    visibleContentBox: parseFramingBox(value.visible_content_box),
    zoomFactor: numberValue(value.zoom_factor),
    anchor:
      anchor.length === 2 && anchor[0] != null && anchor[1] != null
        ? ([anchor[0], anchor[1]] as const)
        : null,
  };
}

function parseCoordinateSpace(value: unknown): FramingCoordinateSpace | null {
  const item = record(value);
  const id = stringValue(item.id ?? item.coordinate_space_id);
  if (!id) return null;
  return {
    id,
    stage: stringValue(item.stage) ?? id,
    assetKey: stringValue(item.asset_key ?? item.storage_key),
    assetHash: stringValue(item.asset_hash ?? item.sha256),
    width: numberValue(item.width),
    height: numberValue(item.height),
    mimeType: stringValue(item.mime_type ?? item.content_type),
    aspectRatio:
      stringValue(item.aspect_ratio) ?? numberValue(item.aspect_ratio),
    parentSpaceId: stringValue(item.parent_space_id),
    transformFromParent: parseTransform(item.transform_from_parent),
    assetHashKind: stringValue(item.asset_hash_kind),
    retentionState: stringValue(item.retention_state) as
      | FramingCoordinateSpace["retentionState"]
      | undefined,
    viewable: booleanValue(item.viewable),
    evidenceRole: stringValue(item.evidence_role) as
      | FramingCoordinateSpace["evidenceRole"]
      | undefined,
    timelineStartSeconds: numberValue(
      item.timeline_start_seconds ?? item.timeline_offset_seconds,
    ),
    timelineEndSeconds: numberValue(item.timeline_end_seconds),
  };
}

function parseObservation(value: unknown, fps = 30): FramingObservation | null {
  const item = record(value);
  const bbox = parseFramingBox(item.bbox ?? item.box);
  const coordinateSpaceId = stringValue(
    item.coordinate_space_id ?? item.space_id,
  );
  if (!bbox || !coordinateSpaceId) return null;
  const frameIndex = numberValue(item.frame_index);
  return {
    subjectId: stringValue(item.subject_id),
    label: stringValue(item.label),
    kind: stringValue(item.kind),
    priority: stringValue(item.priority),
    bbox,
    frameIndex,
    timestampSeconds:
      numberValue(item.timestamp_seconds ?? item.time_seconds) ??
      (frameIndex == null ? null : frameIndex / Math.max(1, fps)),
    confidence: numberValue(item.confidence),
    detector: stringValue(item.detector),
    identityScore: numberValue(item.identity_score),
    coordinateSpaceId,
    associationMethod: stringValue(
      item.association_method ?? item.grounding_method ?? item.provenance,
    ),
    associationReasonCode: stringValue(item.association_reason_code),
    intentSource: stringValue(item.intent_source),
    declarationReason: stringValue(item.declaration_reason),
    identityStatus: stringValue(item.identity_status),
  };
}

function parseIntent(value: unknown): FramingIntentSummary | null {
  if (!isRecord(value)) return null;
  const subjects = (Array.isArray(value.subjects) ? value.subjects : [])
    .filter(isRecord)
    .map(
      (subject): FramingIntentSubject => ({
        subjectId: stringValue(subject.subject_id),
        label: stringValue(subject.label),
        kind: stringValue(subject.kind),
        priority: stringValue(subject.priority),
        identityRequired: booleanValue(subject.identity_required),
        intentSource: stringValue(subject.intent_source),
        declarationReason: stringValue(subject.declaration_reason),
      }),
    );
  const mode = stringValue(value.mode);
  const source = stringValue(value.source ?? value.intent_source);
  const shotSize = stringValue(value.shot_size);
  const trackingAllowed = booleanValue(value.tracking_allowed);
  const cropAllowed = booleanValue(value.crop_allowed);
  const fallbackPolicy = stringList(value.fallback_policy);
  const confidence = numberValue(value.confidence);
  return mode || source || shotSize || subjects.length > 0
    ? {
        mode,
        source,
        shotSize,
        trackingAllowed,
        cropAllowed,
        fallbackPolicy,
        confidence,
        subjects,
      }
    : null;
}

function parseProviderInputReceipt(
  value: unknown,
): ProviderInputReceipt | null {
  if (!isRecord(value)) return null;
  return {
    schemaVersion: stringValue(value.schema_version),
    strategy: stringValue(value.strategy),
    sourceCoordinateSpace: stringValue(value.source_coordinate_space),
    resizedCoordinateSpace: stringValue(value.resized_coordinate_space),
    targetCoordinateSpace: stringValue(value.target_coordinate_space),
    cropWindowPixelsSpace: stringValue(value.crop_window_pixels_space),
    cropWindowNormalizedSpace: stringValue(value.crop_window_normalized_space),
    sourceWidth: numberValue(value.source_width),
    sourceHeight: numberValue(value.source_height),
    resizedWidth: numberValue(value.resized_width),
    resizedHeight: numberValue(value.resized_height),
    targetWidth: numberValue(value.target_width),
    targetHeight: numberValue(value.target_height),
    outputAspectRatio: stringValue(value.output_aspect_ratio),
    requestedAspectRatio: stringValue(value.requested_aspect_ratio),
    cropWindow: parseFramingBox(value.crop_window),
    cropWindowPixels: numberList(value.crop_window_pixels),
    sourceSha256: stringValue(value.source_sha256),
    providerInputSha256: stringValue(value.provider_input_sha256),
    framingTraceId: stringValue(value.framing_trace_id),
  };
}

function parseEvidenceMode(
  evidenceMode: unknown,
  framingMode: unknown,
  appliedReceipt: AppliedFramingReceipt | null,
): FramingEvidenceMode {
  const normalized = stringValue(evidenceMode)?.toLowerCase();
  if (
    normalized &&
    ["shadow", "observe", "observed", "proposed"].includes(normalized)
  ) {
    return "proposed";
  }
  if (
    normalized &&
    ["applied", "actual", "enforce", "enforced"].includes(normalized)
  ) {
    return "actual";
  }
  const normalizedFramingMode = stringValue(framingMode)?.toLowerCase();
  if (normalizedFramingMode === "observe") return "proposed";
  if (normalizedFramingMode === "enforce") return "actual";
  return appliedReceipt ? "actual" : "unknown";
}

function parseSceneKind(
  sceneType: string | null,
  sourceSceneType: string | null,
  finalSceneType: string | null,
  providerTaskExpected: boolean | null,
  providerInputReceipt: ProviderInputReceipt | null,
  coordinateSpaces: FramingCoordinateSpace[],
): FramingSceneKind {
  const normalizedTypes = [sceneType, sourceSceneType, finalSceneType]
    .map((value) => value?.trim().toLowerCase().replaceAll("_", "") ?? "")
    .filter(Boolean);
  if (
    normalizedTypes.some(
      (value) => value.includes("imagetovideo") || value === "i2v",
    )
  ) {
    return "i2v";
  }
  if (providerTaskExpected === true || providerInputReceipt != null)
    return "i2v";
  if (
    normalizedTypes.some(
      (value) =>
        value.includes("staticimage") ||
        value.includes("multiphoto") ||
        value.includes("titlecard"),
    )
  ) {
    return "still";
  }
  if (normalizedTypes.some((value) => value.includes("videoscene"))) {
    return "video";
  }
  if (
    coordinateSpaces.some(
      (space) => normalizeStage(space.stage) === "provider_input",
    )
  ) {
    return "i2v";
  }
  return "unknown";
}

function parseDecision(value: unknown): FramingDecision | null {
  if (!isRecord(value)) return null;
  const declaredFps = numberValue(value.fps);
  const fps = declaredFps ?? 30;
  const legacyDeliverySafeZone = parseFramingBox(value.delivery_safe_zone);
  const safeZones = (Array.isArray(value.safe_zones) ? value.safe_zones : [])
    .map((item): FramingSafeZone | null => {
      if (!isRecord(item)) return null;
      const box = parseFramingBox(item.box ?? item.safe_zone);
      if (!box) return null;
      const rawKind = stringValue(item.kind)?.toLowerCase();
      const kind: FramingSafeZone["kind"] = [
        "provider_input",
        "provider_output",
        "delivery",
      ].includes(rawKind ?? "")
        ? (rawKind as FramingSafeZone["kind"])
        : "unknown";
      return {
        kind,
        coordinateSpaceId: stringValue(
          item.coordinate_space_id ?? item.space_id,
        ),
        box,
      };
    })
    .filter((item): item is FramingSafeZone => item != null);
  if (safeZones.length === 0 && legacyDeliverySafeZone) {
    safeZones.push({
      kind: "delivery",
      coordinateSpaceId: stringValue(
        value.delivery_safe_zone_coordinate_space_id ??
          value.coordinate_space_id ??
          value.space_id,
      ),
      box: legacyDeliverySafeZone,
    });
  }
  return {
    strategy: stringValue(value.strategy),
    coordinateSpaceId: stringValue(value.coordinate_space_id ?? value.space_id),
    cropWindow: parseFramingBox(value.crop_window),
    cropPath: parseCropPath(value.crop_path, fps),
    deliverySafeZone:
      safeZones.find((zone) => zone.kind === "delivery")?.box ??
      legacyDeliverySafeZone,
    safeZones,
    protectedRegion: parseFramingBox(
      value.protected_region ?? value.protected_box,
    ),
    effectViewport: parseEffectViewport(value.effect_viewport, fps),
    visibleContentBox: parseFramingBox(value.visible_content_box),
    fps: declaredFps,
    protectedSubjectIds: stringList(value.protected_subject_ids),
    sacrificedPreferredSubjectIds: stringList(
      value.sacrificed_preferred_subject_ids,
    ),
    fallbackReason: stringValue(value.fallback_reason),
  };
}

function parseReceipt(value: unknown): AppliedFramingReceipt | null {
  if (!isRecord(value)) return null;
  const declaredFps = numberValue(value.fps);
  const fps = declaredFps ?? 30;
  return {
    schemaVersion: stringValue(value.schema_version),
    receiptId: stringValue(value.receipt_id),
    sceneId:
      stringValue(value.scene_id) ??
      (value.scene_id == null ? null : String(value.scene_id)),
    planId: stringValue(value.plan_id),
    planDigest: stringValue(value.plan_digest),
    planRevision: numberValue(value.plan_revision),
    attemptId: stringValue(value.attempt_id),
    taskId: stringValue(value.task_id),
    renderer: stringValue(value.renderer),
    workerId: stringValue(value.worker_id),
    sourceCoordinateSpaceId: stringValue(
      value.source_coordinate_space_id ?? value.coordinate_space_id,
    ),
    sourceAssetHash: stringValue(value.source_asset_hash),
    sourceWidth: numberValue(value.source_width),
    sourceHeight: numberValue(value.source_height),
    deliveryWidth: numberValue(value.delivery_width),
    deliveryHeight: numberValue(value.delivery_height),
    strategy: stringValue(value.strategy),
    cropWindow: parseFramingBox(value.crop_window),
    cropPath: parseCropPath(value.crop_path, fps),
    effectViewport: parseEffectViewport(value.effect_viewport, fps),
    visibleContentBox: parseFramingBox(value.visible_content_box),
    fps: declaredFps,
    durationSeconds: numberValue(value.duration_seconds),
    appliedAt: stringValue(value.applied_at),
    outputAssetHash: stringValue(value.output_asset_hash),
    status: stringValue(value.status),
    issueCodes: stringList(value.issue_codes),
  };
}

function parseQc(value: unknown): FramingQcRecord | null {
  if (!isRecord(value)) return null;
  const metrics = isRecord(value.metrics)
    ? (value.metrics as Record<string, number | string | boolean | null>)
    : null;
  return {
    status: stringValue(value.status),
    issueCodes: stringList(value.issue_codes),
    semanticVerdict: stringValue(value.semantic_verdict),
    deliveredSubjectEvidence: (Array.isArray(value.delivered_subject_evidence)
      ? value.delivered_subject_evidence
      : []
    )
      .map((item): FramingDeliveredSubjectEvidence | null => {
        if (!isRecord(item)) return null;
        const bbox = parseFramingBox(item.bbox);
        if (!bbox) return null;
        return {
          subjectId: stringValue(item.subject_id),
          kind: stringValue(item.kind),
          sampleIndex: numberValue(item.sample_index),
          sceneTimestampSeconds: numberValue(item.scene_timestamp_seconds),
          deliveryTimestampSeconds: numberValue(
            item.delivery_timestamp_seconds,
          ),
          bbox,
          confidence: numberValue(item.confidence),
          matchStatus: stringValue(item.match_status),
          detector: stringValue(item.detector),
          model: stringValue(item.model),
          modelVersion: stringValue(item.model_version),
          coordinateSpaceId: stringValue(item.coordinate_space_id),
        };
      })
      .filter((item): item is FramingDeliveredSubjectEvidence => item != null),
    checkedCoordinateSpaceId: stringValue(
      value.checked_coordinate_space_id ?? value.checked_space_id,
    ),
    sampleCount: numberValue(value.sample_count),
    repairAttempt: numberValue(value.repair_attempt),
    occurredAt: stringValue(value.occurred_at),
    metrics,
  };
}

function parseHistory(
  value: unknown,
  index: number,
): FramingHistoryEvent | null {
  if (!isRecord(value)) return null;
  const eventType = stringValue(value.event_type);
  if (!eventType) return null;
  return {
    id: stringValue(value.id) ?? `${eventType}-${index}`,
    eventType,
    status: stringValue(value.status),
    strategy: stringValue(value.strategy),
    planId: stringValue(value.plan_id ?? value.framing_plan_id),
    planRevision: numberValue(value.plan_revision),
    planDigest: stringValue(value.plan_digest),
    attemptId: stringValue(value.attempt_id),
    taskId: stringValue(value.task_id),
    receiptId: stringValue(value.receipt_id ?? value.applied_receipt_id),
    occurredAt: stringValue(value.occurred_at ?? value.latest_at),
  };
}

function parseArtifact(value: unknown, index: number): FramingArtifact | null {
  if (!isRecord(value)) return null;
  const key = stringValue(value.key ?? value.storage_key ?? value.storage_path);
  const url = stringValue(value.url);
  if (!key && !url) return null;
  return {
    id: stringValue(value.id ?? value.artifact_id) ?? `${key ?? url}-${index}`,
    kind: stringValue(value.kind),
    stage: stringValue(value.stage),
    bucket: stringValue(value.bucket ?? value.storage_bucket),
    key,
    contentType: stringValue(value.content_type ?? value.mime_type),
    sha256: stringValue(value.sha256 ?? value.asset_hash),
    planDigest: stringValue(value.plan_digest),
    attemptId: stringValue(value.attempt_id),
    taskId: stringValue(value.task_id),
    coordinateSpaceId: stringValue(
      value.coordinate_space_id ?? value.source_coordinate_space_id,
    ),
    url,
    expiresIn: numberValue(value.expires_in),
    signedAt: stringValue(value.signed_at),
    urlExpiresAt: stringValue(value.url_expires_at),
    retentionClass: stringValue(value.retention_class),
    expiresAt: stringValue(value.expires_at),
    evidenceRole: stringValue(value.evidence_role) as
      | FramingArtifact["evidenceRole"]
      | undefined,
  };
}

function parseAttemptSummary(value: unknown): FramingAttemptSummary | null {
  if (!isRecord(value)) return null;
  const attemptId = stringValue(value.attempt_id ?? value.attemptId);
  if (!attemptId) return null;
  const rawAvailability = stringValue(
    value.evidence_availability ?? value.evidenceAvailability,
  );
  const status = stringValue(value.status) ?? "unknown";
  return {
    attemptId,
    ordinal: numberValue(value.ordinal),
    status: (
      ["accepted", "rejected", "failed", "repaired", "fallback"] as const
    ).includes(status as never)
      ? (status as FramingAttemptStatus)
      : "unknown",
    startedAt: stringValue(value.started_at ?? value.startedAt),
    completedAt: stringValue(value.completed_at ?? value.completedAt),
    planDigest: stringValue(value.plan_digest ?? value.planDigest),
    receiptDigest: stringValue(value.receipt_digest ?? value.receiptDigest),
    reportDigest: stringValue(value.report_digest ?? value.reportDigest),
    evidenceAvailability: parseFramingAvailability(rawAvailability),
    rawAvailability,
    isDefault: value.is_default === true || value.isDefault === true,
  };
}

function parseCapabilities(value: unknown): FramingCapabilities | null {
  if (!isRecord(value)) return null;
  const flag = (key: string) => value[key] === true;
  return {
    exactSource: flag("exact_source"),
    workingSource: flag("working_source"),
    providerInput: flag("provider_input"),
    generatedRoot: flag("generated_root"),
    appliedReceipt: flag("applied_receipt"),
    deliveryPixels: flag("delivery_pixels"),
    subjectVerdicts: flag("subject_verdicts"),
    attemptHistory: flag("attempt_history"),
    preciseVideoTiming: flag("precise_video_timing"),
    contactSheetFallback: flag("contact_sheet_fallback"),
  };
}

const STAGE_AVAILABILITY_KEYS: readonly FramingStageAvailabilityKey[] = [
  "source_native",
  "source_working",
  "provider_input",
  "provider_output",
  "post_effect",
  "encoded_delivery",
];

function parseStageAvailability(
  value: unknown,
): Partial<Record<FramingStageAvailabilityKey, FramingAvailability>> {
  if (!isRecord(value)) return {};
  const availability: Partial<
    Record<FramingStageAvailabilityKey, FramingAvailability>
  > = {};
  for (const key of STAGE_AVAILABILITY_KEYS) {
    if (key in value) {
      availability[key] = parseFramingAvailability(value[key]);
    }
  }
  return availability;
}

export function parseFramingSceneInspector(
  value: unknown,
): FramingSceneInspector {
  const envelope = record(value);
  const data = record(envelope.data ?? envelope);
  const currentRaw = record(data.current);
  const plan = record(data.plan);
  const provenance = record(data.provenance ?? plan.provenance);
  const current = parseHistory(
    {
      id: currentRaw.id ?? "current",
      event_type: currentRaw.event_type ?? "scene.framing.current",
      framing_plan_id:
        currentRaw.framing_plan_id ?? currentRaw.plan_id ?? plan.id,
      plan_revision: currentRaw.plan_revision ?? plan.revision,
      plan_digest: currentRaw.plan_digest ?? plan.digest,
      config_release_id: currentRaw.config_release_id ?? plan.config_release_id,
      config_digest: currentRaw.config_digest ?? plan.config_digest,
      config_schema_version:
        currentRaw.config_schema_version ?? plan.config_schema_version,
      ...currentRaw,
      attempt_id: currentRaw.attempt_id ?? data.attempt_id ?? plan.attempt_id,
      task_id: currentRaw.task_id ?? data.task_id ?? plan.task_id,
    },
    0,
  )!;
  const decisionRaw = data.decision ?? plan.decision;
  const spacesRaw =
    data.coordinate_spaces ?? plan.spaces ?? plan.coordinate_spaces;
  const observationsRaw = data.observations ?? plan.observations;
  const receiptRaw =
    data.applied_receipt ?? data.receipt ?? plan.applied_receipt;
  const decision = parseDecision(decisionRaw);
  const appliedReceipt = parseReceipt(receiptRaw);
  const framingFps = decision?.fps ?? appliedReceipt?.fps ?? 30;
  const source = isRecord(data.source) ? data.source : null;
  const intentRaw =
    data.intent ??
    plan.intent ??
    source?.framing_intent ??
    source?.intent ??
    source;
  const coordinateSpaces = (Array.isArray(spacesRaw) ? spacesRaw : [])
    .map(parseCoordinateSpace)
    .filter((item): item is FramingCoordinateSpace => item != null);
  const providerInputReceipt = parseProviderInputReceipt(
    data.provider_input_receipt ??
      plan.provider_input_receipt ??
      provenance.provider_input_receipt ??
      provenance.provider_internal_receipt,
  );
  const sourceSceneType = stringValue(
    data.source_scene_type ??
      plan.source_scene_type ??
      provenance.source_scene_type,
  );
  const finalSceneType = stringValue(
    data.final_scene_type ??
      plan.final_scene_type ??
      provenance.final_scene_type,
  );
  const sceneType = stringValue(
    data.scene_type ??
      plan.scene_type ??
      provenance.scene_type ??
      sourceSceneType ??
      finalSceneType,
  );
  const explicitProviderTaskExpected = booleanValue(
    data.provider_task_expected ??
      plan.provider_task_expected ??
      provenance.provider_task_expected,
  );
  const sceneKind = parseSceneKind(
    sceneType,
    sourceSceneType,
    finalSceneType,
    explicitProviderTaskExpected,
    providerInputReceipt,
    coordinateSpaces,
  );
  const providerTaskExpected =
    explicitProviderTaskExpected ??
    (sceneKind === "i2v"
      ? true
      : sceneKind === "still" || sceneKind === "video"
        ? false
        : null);
  const explicitProviderReceiptExpected = booleanValue(
    data.provider_internal_receipt_expected ??
      plan.provider_internal_receipt_expected ??
      provenance.provider_internal_receipt_expected,
  );
  const providerReceiptExpected =
    explicitProviderReceiptExpected ??
    (providerTaskExpected === false ||
    sceneKind === "still" ||
    sceneKind === "video"
      ? false
      : null);
  const framingMode = stringValue(
    data.framing_mode ?? plan.framing_mode ?? provenance.cohort_mode,
  );
  const evidenceMode = parseEvidenceMode(
    data.evidence_mode ?? plan.evidence_mode ?? provenance.evidence_mode,
    framingMode,
    appliedReceipt,
  );

  return {
    schemaVersion: stringValue(data.schema_version) ?? "1.0",
    sessionId: String(data.session_id ?? ""),
    sceneId: String(data.scene_id ?? ""),
    sceneType,
    sourceSceneType: sourceSceneType ?? sceneType,
    finalSceneType: finalSceneType ?? sceneType,
    sceneKind,
    framingMode,
    evidenceMode,
    providerTaskExpected,
    providerReceiptExpected,
    attemptId: stringValue(data.attempt_id ?? currentRaw.attempt_id),
    current: {
      ...current,
      checkedCoordinateSpaceId: stringValue(
        currentRaw.checked_coordinate_space_id ?? currentRaw.checked_space_id,
      ),
      configReleaseId: stringValue(
        currentRaw.config_release_id ?? plan.config_release_id,
      ),
      configDigest: stringValue(currentRaw.config_digest ?? plan.config_digest),
      configSchemaVersion: scalarStringValue(
        currentRaw.config_schema_version ?? plan.config_schema_version,
      ),
    },
    intent: parseIntent(intentRaw),
    providerInputReceipt,
    provenance,
    coordinateSpaces,
    observations: (Array.isArray(observationsRaw) ? observationsRaw : [])
      .map((item) => parseObservation(item, framingFps))
      .filter((item): item is FramingObservation => item != null),
    decision,
    appliedReceipt,
    qcHistory: (Array.isArray(data.qc_history) ? data.qc_history : [])
      .map(parseQc)
      .filter((item): item is FramingQcRecord => item != null),
    history: (Array.isArray(data.history) ? data.history : [])
      .map(parseHistory)
      .filter((item): item is FramingHistoryEvent => item != null)
      .sort((left, right) =>
        String(left.occurredAt ?? "").localeCompare(
          String(right.occurredAt ?? ""),
        ),
      ),
    artifacts: (Array.isArray(data.artifacts)
      ? data.artifacts
      : Array.isArray(data.artifact_refs)
        ? data.artifact_refs
        : Array.isArray(plan.artifact_refs)
          ? plan.artifact_refs
          : []
    )
      .map(parseArtifact)
      .filter((item): item is FramingArtifact => item != null),
    source,
    report: isRecord(data.report) ? data.report : null,
    attempts: (Array.isArray(data.attempts) ? data.attempts : [])
      .map(parseAttemptSummary)
      .filter((item): item is FramingAttemptSummary => item != null),
    attemptsTotal: numberValue(data.attempts_total),
    attemptsTruncated: data.attempts_truncated === true,
    attemptAvailability:
      data.attempt_availability == null
        ? null
        : parseFramingAvailability(data.attempt_availability),
    capabilities: parseCapabilities(data.capabilities),
    stageAvailability: parseStageAvailability(data.stage_availability),
    historyReturned: numberValue(data.history_returned),
    historyCap: numberValue(data.history_cap),
    historyTruncated: data.history_truncated === true,
  };
}

function normalizeStage(value?: string | null): string {
  return (value ?? "").trim().toLowerCase().replaceAll("-", "_");
}

function stageMatches(
  value: string | null | undefined,
  aliases: readonly string[],
) {
  const normalized = normalizeStage(value);
  return aliases.some(
    (alias) => normalized === alias || normalized.includes(alias),
  );
}

function normalizedStorageKey(value?: string | null): string | null {
  if (!value) return null;
  return value.replaceAll("\\", "/").replace(/^\/+/, "");
}

function artifactMatchesCurrent(
  artifact: FramingArtifact,
  inspector: FramingSceneInspector,
): boolean {
  const attemptId = inspector.attemptId ?? inspector.current.attemptId;
  return (
    (!artifact.attemptId || artifact.attemptId === attemptId) &&
    (!artifact.taskId || artifact.taskId === inspector.current.taskId) &&
    (!artifact.planDigest ||
      artifact.planDigest === inspector.current.planDigest)
  );
}

function artifactMatchesSpace(
  artifact: FramingArtifact,
  coordinateSpace: FramingCoordinateSpace,
): boolean {
  if (
    artifact.coordinateSpaceId &&
    artifact.coordinateSpaceId !== coordinateSpace.id
  ) {
    return false;
  }
  if (coordinateSpace.assetHash) {
    return artifact.sha256 === coordinateSpace.assetHash;
  }
  const artifactKey = normalizedStorageKey(artifact.key);
  const spaceKey = normalizedStorageKey(coordinateSpace.assetKey);
  return Boolean(artifactKey && spaceKey && artifactKey === spaceKey);
}

export function framingSpaceHasRetainedPixels(
  coordinateSpace: FramingCoordinateSpace | null,
): boolean {
  return coordinateSpace?.retentionState === "retained";
}

export function framingSpaceCanBeViewed(
  coordinateSpace: FramingCoordinateSpace | null,
): boolean {
  if (!coordinateSpace) return false;
  if (coordinateSpace.viewable === false) return false;
  return coordinateSpace.retentionState !== "ephemeral";
}

export function buildFramingStageViews(
  inspector: FramingSceneInspector,
): FramingStageView[] {
  return STAGES.map((stage) => {
    const candidates = [...inspector.coordinateSpaces]
      .reverse()
      .filter(
        (space) =>
          stageMatches(space.stage, stage.aliases) ||
          stageMatches(space.id, stage.aliases),
      )
      .map((coordinateSpace) => ({
        coordinateSpace,
        artifact: framingSpaceCanBeViewed(coordinateSpace)
          ? (inspector.artifacts.find(
              (item) =>
                artifactMatchesCurrent(item, inspector) &&
                artifactMatchesSpace(item, coordinateSpace),
            ) ?? null)
          : null,
      }));
    const selected =
      candidates.find((candidate) => candidate.artifact != null) ??
      candidates.find(
        ({ coordinateSpace }) =>
          coordinateSpace.retentionState === "retained" &&
          coordinateSpace.viewable !== false,
      ) ??
      candidates[0];
    return {
      ...stage,
      coordinateSpace: selected?.coordinateSpace ?? null,
      artifact: selected?.artifact ?? null,
    };
  });
}

export function framingInspectorWarnings(
  inspector: FramingSceneInspector,
): string[] {
  return verifyFramingEvidence(inspector).issues;
}

const NO_CROP_STRATEGIES = new Set([
  "contain",
  "contain_blur",
  "postcard",
  "static_source",
]);

function compareRequired<T>(
  missing: string[],
  mismatches: string[],
  label: string,
  left: T | null | undefined,
  right: T | null | undefined,
) {
  if (left == null || right == null || left === "" || right === "") {
    missing.push(`${label} is missing from the plan/current state or receipt.`);
  } else if (left !== right) {
    mismatches.push(`${label} does not match the applied receipt.`);
  }
}

function compareOptionalBox(
  mismatches: string[],
  label: string,
  left: FramingBox | null | undefined,
  right: FramingBox | null | undefined,
  width: number,
  height: number,
) {
  if (Boolean(left) !== Boolean(right)) {
    mismatches.push(`${label} exists on only one side of the plan/receipt.`);
  } else if (
    left &&
    right &&
    !framingBoxesEqualInPixels(left, right, width, height)
  ) {
    mismatches.push(`${label} differs from the applied receipt.`);
  }
}

function cropPathsEqual(
  left: FramingCropPathPoint[],
  right: FramingCropPathPoint[],
  width: number,
  height: number,
): boolean {
  if (left.length !== right.length) return false;
  return left.every((point, index) => {
    const other = right[index];
    const frameMatches =
      point.frameIndex != null || other?.frameIndex != null
        ? point.frameIndex != null &&
          other?.frameIndex != null &&
          point.frameIndex === other.frameIndex
        : other != null &&
          Math.abs(point.timeSeconds - other.timeSeconds) <= 1 / 30;
    return (
      other != null &&
      frameMatches &&
      framingBoxesEqualInPixels(point.window, other.window, width, height)
    );
  });
}

export function verifyFramingEvidence(
  inspector: FramingSceneInspector,
): FramingVerification {
  const missing: string[] = [];
  const mismatches: string[] = [];
  const { decision, appliedReceipt: receipt, current } = inspector;
  const attemptId = inspector.attemptId ?? current.attemptId;

  if (!decision) missing.push("No resolved framing decision is available.");
  if (!attemptId || attemptId === "legacy") {
    missing.push(
      "No stable scene attempt id is recorded for this framing plan.",
    );
  }
  if (inspector.evidenceMode === "proposed") {
    if (!decision) return { status: "unknown", issues: missing };
    return {
      status: "proposed",
      issues: [
        "Observe-mode geometry is a proposal over the actual legacy output; it was not sent to the renderer.",
        ...missing,
      ],
    };
  }
  if (!receipt) {
    missing.push("No applied renderer receipt is available for this scene.");
  }
  if (!decision || !receipt) {
    return { status: "unknown", issues: [...mismatches, ...missing] };
  }

  compareRequired(
    missing,
    mismatches,
    "Plan id",
    current.planId,
    receipt.planId,
  );
  compareRequired(
    missing,
    mismatches,
    "Plan revision",
    current.planRevision,
    receipt.planRevision,
  );
  compareRequired(
    missing,
    mismatches,
    "Plan digest",
    current.planDigest,
    receipt.planDigest,
  );
  compareRequired(
    missing,
    mismatches,
    "Attempt id",
    attemptId,
    receipt.attemptId,
  );
  if (inspector.providerTaskExpected === true) {
    compareRequired(
      missing,
      mismatches,
      "Provider task id",
      current.taskId,
      receipt.taskId,
    );
    const providerReceipt = inspector.providerInputReceipt;
    if (!providerReceipt && inspector.providerReceiptExpected === true) {
      missing.push(
        "No provider-input crop receipt is available for this I2V scene.",
      );
    } else if (providerReceipt) {
      const providerSourceSpace = providerReceipt.sourceSha256
        ? inspector.coordinateSpaces.find(
            (space) => space.assetHash === providerReceipt.sourceSha256,
          )
        : null;
      const providerTargetSpace = providerReceipt.providerInputSha256
        ? inspector.coordinateSpaces.find(
            (space) => space.assetHash === providerReceipt.providerInputSha256,
          )
        : null;
      if (!providerSourceSpace) {
        missing.push(
          "The provider receipt source coordinate space is not recorded.",
        );
      } else {
        compareRequired(
          missing,
          mismatches,
          "Provider receipt source width",
          providerSourceSpace.width,
          providerReceipt.sourceWidth,
        );
        compareRequired(
          missing,
          mismatches,
          "Provider receipt source height",
          providerSourceSpace.height,
          providerReceipt.sourceHeight,
        );
      }
      if (!providerTargetSpace) {
        missing.push(
          "The provider-internal target coordinate space is not recorded.",
        );
      } else {
        compareRequired(
          missing,
          mismatches,
          "Provider target width",
          providerTargetSpace.width,
          providerReceipt.targetWidth,
        );
        compareRequired(
          missing,
          mismatches,
          "Provider target height",
          providerTargetSpace.height,
          providerReceipt.targetHeight,
        );
      }
    }
  } else if (current.taskId || receipt.taskId) {
    compareRequired(
      missing,
      mismatches,
      "Provider task id",
      current.taskId,
      receipt.taskId,
    );
  }
  compareRequired(
    missing,
    mismatches,
    "Receipt id",
    current.receiptId,
    receipt.receiptId,
  );
  compareRequired(
    missing,
    mismatches,
    "Scene id",
    inspector.sceneId,
    receipt.sceneId,
  );
  compareRequired(
    missing,
    mismatches,
    "Framing strategy",
    decision.strategy,
    receipt.strategy,
  );
  compareRequired(
    missing,
    mismatches,
    "Source coordinate space",
    decision.coordinateSpaceId,
    receipt.sourceCoordinateSpaceId,
  );

  const sourceSpace = receipt.sourceCoordinateSpaceId
    ? inspector.coordinateSpaces.find(
        (space) => space.id === receipt.sourceCoordinateSpaceId,
      )
    : null;
  if (!sourceSpace) {
    missing.push("The receipt source coordinate space is not recorded.");
  } else {
    compareRequired(
      missing,
      mismatches,
      "Source asset hash",
      sourceSpace.assetHash,
      receipt.sourceAssetHash,
    );
    compareRequired(
      missing,
      mismatches,
      "Source width",
      sourceSpace.width,
      receipt.sourceWidth,
    );
    compareRequired(
      missing,
      mismatches,
      "Source height",
      sourceSpace.height,
      receipt.sourceHeight,
    );
  }

  const width = receipt.sourceWidth ?? sourceSpace?.width ?? 1;
  const height = receipt.sourceHeight ?? sourceSpace?.height ?? 1;
  compareOptionalBox(
    mismatches,
    "Applied crop window",
    decision.cropWindow,
    receipt.cropWindow,
    width,
    height,
  );
  if (
    !NO_CROP_STRATEGIES.has(decision.strategy ?? "") &&
    !decision.cropWindow &&
    decision.cropPath.length === 0
  ) {
    missing.push(
      "The crop strategy has neither a crop window nor a crop path.",
    );
  }
  if (!cropPathsEqual(decision.cropPath, receipt.cropPath, width, height)) {
    mismatches.push(
      "The applied crop path or timestamps differ from the plan.",
    );
  }
  if (
    decision.cropPath.length > 0 ||
    decision.effectViewport?.cropPath.length
  ) {
    compareRequired(
      missing,
      mismatches,
      "Frame rate",
      decision.fps,
      receipt.fps,
    );
    if (receipt.durationSeconds == null) {
      missing.push("The receipt duration is missing for temporal framing.");
    }
  }
  if (!decision.visibleContentBox || !receipt.visibleContentBox) {
    missing.push("The delivery visible-content box is missing.");
  } else {
    compareOptionalBox(
      mismatches,
      "Visible content box",
      decision.visibleContentBox,
      receipt.visibleContentBox,
      receipt.deliveryWidth ?? 1,
      receipt.deliveryHeight ?? 1,
    );
  }
  if (!decision.effectViewport && !receipt.effectViewport) {
    missing.push(
      "The explicit effect viewport is missing from the plan or receipt.",
    );
  } else if (!decision.effectViewport || !receipt.effectViewport) {
    mismatches.push(
      "The effect viewport exists on only one side of the plan/receipt.",
    );
  } else {
    compareRequired(
      missing,
      mismatches,
      "Effect type",
      decision.effectViewport.effectType,
      receipt.effectViewport.effectType,
    );
    compareRequired(
      missing,
      mismatches,
      "Effect coordinate space",
      decision.effectViewport.coordinateSpaceId,
      receipt.effectViewport.coordinateSpaceId,
    );
    const effectSpaceId = receipt.effectViewport.coordinateSpaceId;
    const effectSpaceRecorded = inspector.coordinateSpaces.some(
      (space) =>
        space.id === effectSpaceId ||
        (effectSpaceId === "delivery_canvas" &&
          space.stage.toLowerCase().replaceAll("-", "_") ===
            "pre_effect_delivery"),
    );
    if (!effectSpaceRecorded) {
      missing.push("The effect coordinate space is not recorded.");
    }
    compareOptionalBox(
      mismatches,
      "Effect crop window",
      decision.effectViewport.cropWindow,
      receipt.effectViewport.cropWindow,
      receipt.deliveryWidth ?? 1,
      receipt.deliveryHeight ?? 1,
    );
    if (
      !cropPathsEqual(
        decision.effectViewport.cropPath,
        receipt.effectViewport.cropPath,
        receipt.deliveryWidth ?? 1,
        receipt.deliveryHeight ?? 1,
      )
    ) {
      mismatches.push(
        "The applied effect path or timestamps differ from the plan.",
      );
    }
    if (
      decision.effectViewport.effectType !== "none" &&
      (!decision.effectViewport.visibleContentBox ||
        !receipt.effectViewport.visibleContentBox)
    ) {
      missing.push("The effect visible-content box is missing.");
    } else {
      compareOptionalBox(
        mismatches,
        "Effect visible content box",
        decision.effectViewport.visibleContentBox,
        receipt.effectViewport.visibleContentBox,
        receipt.deliveryWidth ?? 1,
        receipt.deliveryHeight ?? 1,
      );
    }
  }

  if (!receipt.status) {
    missing.push("The receipt status is missing.");
  } else if (!new Set(["applied", "passed", "success"]).has(receipt.status)) {
    mismatches.push(`The receipt status is ${receipt.status}, not applied.`);
  }
  if (receipt.issueCodes.length > 0) {
    mismatches.push(
      `The receipt reports issues: ${receipt.issueCodes.join(", ")}.`,
    );
  }

  const qc = inspector.qcHistory.at(-1) ?? null;
  if (!qc) {
    missing.push("No delivered-pixel QC record is available.");
  } else {
    if (!qc.status) missing.push("The delivered-pixel QC status is missing.");
    else if (!new Set(["passed", "repaired"]).has(qc.status)) {
      mismatches.push(`Delivered-pixel QC status is ${qc.status}.`);
    }
    if (qc.issueCodes.length > 0) {
      mismatches.push(
        `Delivered-pixel QC reports: ${qc.issueCodes.join(", ")}.`,
      );
    }
    compareRequired(
      missing,
      mismatches,
      "Checked coordinate space",
      current.checkedCoordinateSpaceId,
      qc.checkedCoordinateSpaceId,
    );
    const checkedSpace = qc.checkedCoordinateSpaceId
      ? inspector.coordinateSpaces.find(
          (space) => space.id === qc.checkedCoordinateSpaceId,
        )
      : null;
    if (!checkedSpace) {
      missing.push("The delivered-pixel QC coordinate space is not recorded.");
    } else {
      compareRequired(
        missing,
        mismatches,
        "Output asset hash",
        checkedSpace.assetHash,
        receipt.outputAssetHash,
      );
      compareRequired(
        missing,
        mismatches,
        "Delivery width",
        checkedSpace.width,
        receipt.deliveryWidth,
      );
      compareRequired(
        missing,
        mismatches,
        "Delivery height",
        checkedSpace.height,
        receipt.deliveryHeight,
      );
    }
    if (qc.metrics?.render_receipt_match == null) {
      missing.push("QC did not record a positive renderer-receipt match.");
    } else if (qc.metrics.render_receipt_match !== true) {
      mismatches.push("QC reports that the renderer receipt did not match.");
    }
  }

  if (mismatches.length > 0) {
    return { status: "mismatch", issues: [...mismatches, ...missing] };
  }
  if (missing.length > 0) return { status: "unknown", issues: missing };
  return { status: "verified", issues: [] };
}

export function framingBoxesEqual(
  left: FramingBox,
  right: FramingBox,
  tolerance = 0.001,
): boolean {
  return (
    Math.abs(left.xMin - right.xMin) <= tolerance &&
    Math.abs(left.yMin - right.yMin) <= tolerance &&
    Math.abs(left.xMax - right.xMax) <= tolerance &&
    Math.abs(left.yMax - right.yMax) <= tolerance
  );
}

export function framingBoxesEqualInPixels(
  left: FramingBox,
  right: FramingBox,
  width: number,
  height: number,
): boolean {
  const xTolerance = 1 / Math.max(1, width);
  const yTolerance = 1 / Math.max(1, height);
  return (
    Math.abs(left.xMin - right.xMin) <= xTolerance &&
    Math.abs(left.yMin - right.yMin) <= yTolerance &&
    Math.abs(left.xMax - right.xMax) <= xTolerance &&
    Math.abs(left.yMax - right.yMax) <= yTolerance
  );
}
